import axios from 'axios';

/** Create validation fields the Review deny path surfaces with localized copy. */
export type CreateShipmentDenyField = 'pickupType' | 'dropOffInformation';

export interface CreateShipmentFieldError {
  field?: CreateShipmentDenyField;
  message: string;
}

type TranslateFn = (key: string, params?: Record<string, string>) => string;

const DENY_FIELD_I18N: Record<CreateShipmentDenyField, string> = {
  pickupType: 'createShipment.reviewAndPay.paymentDeny.pickupTypeRequired',
  dropOffInformation: 'createShipment.reviewAndPay.paymentDeny.dropOffInformationRequired',
};

const SUPPORT_PHONE_KEY = 'createShipment.serviceStep.pickupDropOff.availability.supportPhone';

/**
 * Reads the first pickupType / dropOffInformation validation error from a create 400 body.
 * Other 400s stay opaque so Review keeps showing the generic deny path.
 */
export function extractCreateShipmentFieldError(error: unknown): CreateShipmentFieldError | null {
  if (!axios.isAxiosError(error) || error.response?.status !== 400) {
    return null;
  }

  const payload = error.response.data as
    { errors?: { field?: string; message?: string }[] } | undefined;
  const errors = payload?.errors;
  if (!Array.isArray(errors)) {
    return null;
  }

  for (const item of errors) {
    if (item?.field !== 'pickupType' && item?.field !== 'dropOffInformation') {
      continue;
    }
    const message = typeof item.message === 'string' ? item.message.trim() : '';
    if (!message) {
      continue;
    }
    return { field: item.field, message };
  }

  return null;
}

/**
 * Replaces `{{phone}}` placeholders in backend create validation messages.
 */
export function interpolateCreateErrorPhone(message: string, phone: string): string {
  return message.replaceAll('{{phone}}', phone);
}

/**
 * Resolves the Review payment-deny body for a create failure.
 * Field errors use localized copy; card declines keep the decline message;
 * other processing failures use shipment-failed copy with refund and support phone.
 */
export function resolveCreateDenyMessage(
  result: { message: string; code?: string; field?: CreateShipmentDenyField },
  translate: TranslateFn,
): string {
  const phone = translate(SUPPORT_PHONE_KEY);

  if (result.field && result.field in DENY_FIELD_I18N) {
    return translate(DENY_FIELD_I18N[result.field], { phone });
  }

  if (result.code === 'PAYMENT_DECLINED') {
    const trimmed = result.message?.trim() ?? '';
    if (trimmed) {
      return interpolateCreateErrorPhone(trimmed, phone);
    }
  }

  return translate('createShipment.reviewAndPay.paymentDeny.shipmentFailed', { phone });
}

/** i18n title for credit-card authorization declines only. */
export const PAYMENT_DENY_TITLE_KEY = 'createShipment.reviewAndPay.paymentDeny.title';

/** i18n title when shipment processing fails after (or without) a payment decline. */
export const SHIPMENT_FAILED_TITLE_KEY =
  'createShipment.reviewAndPay.paymentDeny.shipmentFailedTitle';

/**
 * Picks the Review deny-modal title: authorization copy only for PAYMENT_DECLINED.
 */
export function resolveCreateDenyTitleKey(result: {
  code?: string;
  message?: string;
  field?: CreateShipmentDenyField;
}): string {
  if (result.code === 'PAYMENT_DECLINED') {
    return PAYMENT_DENY_TITLE_KEY;
  }
  return SHIPMENT_FAILED_TITLE_KEY;
}
